import { useEffect, useState, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

/**
 * M11 L11.08 — Compliance Validation Screen.
 * Displays 8-point brand compliance scoring.
 */
interface ComplianceResult {
  readonly verificationId: string;
  readonly jobRequestId: string;
  readonly brandComplianceScore: number | null;
  readonly criteria: ComplianceCriterion[];
  readonly status: string;
  readonly validatedAt: string | null;
}

interface ComplianceCriterion {
  readonly name: string;
  readonly score: number;
  readonly maxScore: number;
  readonly remarks: string | null;
}

const DEFAULT_CRITERIA = [
  'Color Match', 'Logo Placement', 'Tagline Visibility', 'Font Compliance',
  'Border Alignment', 'Surface Preparation', 'Coat Coverage', 'Overall Finish',
];

export function ComplianceValidationPage(): ReactNode {
  const { decimal, number } = useBusinessNumber();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { atDay } = useBusinessDay();
  const { verificationId } = useParams<{ verificationId: string }>();
  const auth = useAuth();
  const [data, setData] = useState<ComplianceResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<ComplianceResult>(`/v1/closure/review/${verificationId}/compliance`, { token: auth.token })
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setError(e?.message ?? 'Failed to load compliance data'); });
    return () => { alive = false; };
  }, [auth.token, verificationId]);

  if (error) return <main aria-label="compliance-validation"><p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p></main>;
  if (!data) return <main aria-label="compliance-validation"><p>{t('closure.compliance.loading')}</p></main>;

  const criteria = data.criteria.length > 0 ? data.criteria : DEFAULT_CRITERIA.map((n) => ({ name: n, score: 0, maxScore: 10, remarks: null }));

  return (
    <main aria-label="compliance-validation">
      <h2>{t('closure.compliance.title')}</h2>
      <p><Link to={`/closure/final/review/${verificationId}`}>{t('closure.compliance.back')}</Link></p>

      <div style={{ marginBottom: 16, padding: 16, backgroundColor: 'var(--color-info-bg)', borderRadius: 8 }}>
        <strong>{t('closure.compliance.overall')} </strong>
        <span style={{ fontSize: 24, fontWeight: 'bold', color: (data.brandComplianceScore ?? 0) >= 70 ? 'var(--color-success)' : 'var(--color-danger)' }}>
          {decimal(data.brandComplianceScore)} / 100
        </span>
      </div>

      <table style={{ borderCollapse: 'collapse', width: '100%', maxWidth: 600 }}>
        <thead>
          <tr>
            <th style={th}>{t('closure.compliance.col.criterion')}</th>
            <th style={{ ...th, textAlign: 'center' }}>{t('closure.compliance.col.score')}</th>
            <th style={{ ...th, textAlign: 'center' }}>{t('closure.compliance.col.max')}</th>
            <th style={th}>{t('closure.compliance.col.remarks')}</th>
          </tr>
        </thead>
        <tbody>
          {criteria.map((c) => (
            <tr key={c.name} data-testid={`criterion-${c.name.replace(/\s/g, '-').toLowerCase()}`}>
              <td style={td}>{c.name}</td>
              <td style={{ ...td, textAlign: 'center' }}>{number(c.score)}</td>
              <td style={{ ...td, textAlign: 'center' }}>{number(c.maxScore)}</td>
              <td style={td}>{c.remarks ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ marginTop: 12, fontSize: 13, color: 'var(--color-text-secondary)' }}>
        {t('closure.compliance.status')} <strong>{data.status}</strong>
        {data.validatedAt && <> | {t('closure.compliance.validated')} {atDay(data.validatedAt)}</>}
      </div>
    </main>
  );
}

const th: React.CSSProperties = { textAlign: 'left', padding: '8px 12px', borderBottom: '2px solid var(--color-border-default)' };
const td: React.CSSProperties = { padding: '8px 12px', borderBottom: '1px solid var(--color-border-default)' };
